import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '@library/components/card/Card';
import {
  PlayerImage,
  PlayerName,
  PlayerProfile
} from '@library/components/player';
import { Button } from '@library/components/buttons';
import { DateTime } from 'luxon';
import type { GameData } from '@library/types/games';
import type { LeagueTeamPlayer } from '@library/types/player';
import { Loading } from '@library/components/Loading';
import { NoResults } from '@library/components/NoResults';
import { StatusIndicator } from '@library/components/StatusIndicator';
import chevronDownIcon from '@library/assets/images/icons/chevron-down-icon.svg';
import { classNames } from '@library/utils';
import { isTimeAfter } from '@library/utils/dateTime';
import { useGetPlayerNewsQuery } from '@bo-services/simulationsApi';
import { useState } from 'react';

export const NewsInjuryReport = ({
  game,
  leagueId,
  gamePlayers
}: {
  game: GameData;
  gamePlayers: Array<LeagueTeamPlayer>;
  leagueId: string;
}) => {
  const [showNews, setShowNews] = useState(false);

  const {
    data: homeTeamNews,
    isSuccess: isHomeSuccess,
    isLoading: isHomeLoading,
    isFetching: isHomeFetching
  } = useGetPlayerNewsQuery({
    league_id: leagueId,
    from_date:
      DateTime.fromISO(game.scheduled_at).minus({ months: 2 }).toISODate() ||
      game.scheduled_at,
    to_date: game.scheduled_at,
    team: game.home_team_name
  });

  const {
    data: awayTeamNews,
    isSuccess: isAwaySuccess,
    isLoading: isAwayLoading,
    isFetching: isAwayFetching
  } = useGetPlayerNewsQuery({
    league_id: leagueId,
    from_date:
      DateTime.fromISO(game.scheduled_at).minus({ months: 2 }).toISODate() ||
      game.scheduled_at,
    to_date: game.scheduled_at,
    team: game.away_team_name
  });

  const isNewsSuccess = isHomeSuccess && isAwaySuccess;
  const isNewsLoading =
    isHomeLoading || isHomeFetching || isAwayLoading || isAwayFetching;

  const { articles: awayTeamArticles } = awayTeamNews || {};
  const { articles: homeTeamArticles } = homeTeamNews || {};

  const news = [...(homeTeamArticles || []), ...(awayTeamArticles || [])].sort(
    (a, b) =>
      isTimeAfter({
        dateValue: DateTime.fromISO(a.posted_at),
        dateToCompareAgainst: DateTime.fromISO(b.posted_at)
      })
        ? -1
        : 1
  );

  return (
    <Card className="text-soClean-100">
      <CardHeader>
        <CardTitle>News and Injury report</CardTitle>
        <div className="flex gap-4">
          <Button
            type="button"
            title="ToggleNewsButton"
            onClick={() => setShowNews(!showNews)}
          >
            <img
              alt="Chevron Down icon"
              className={classNames(showNews ? 'rotate-180' : '')}
              src={chevronDownIcon}
            />
          </Button>
        </div>
      </CardHeader>
      {showNews && (
        <CardContent className="grid grid-flow-col grid-rows-2 gap-4 overflow-x-scroll overscroll-x-none">
          {news.map((article) => {
            const rosterPlayer = gamePlayers.find(
              (player) => player.foreign_id === article.player.id
            );

            const { image_url: image, team, position } = rosterPlayer || {};

            const { headline, injury, player } = article;
            const { first_name: firstName, last_name: lastName } = player;

            return (
              <PlayerProfile
                key={article.id}
                className="bg-soFresh-120 flex h-40 w-48 flex-row gap-2 p-4"
                name={`${firstName} ${lastName}` || ''}
                imageUrl={image}
              >
                <PlayerImage className="size-6 rounded-full" />
                <div className="flex flex-col gap-1.5">
                  <div className="flex flex-col leading-3">
                    <PlayerName className="text-sm font-semibold" />
                    <span className="text-2xs text-soClean-170">
                      {`${position} - ${team}`}
                    </span>
                  </div>

                  <span className="text-xs">{headline}</span>
                  {injury && (
                    <div className="flex flex-row items-center gap-2">
                      <StatusIndicator status={injury.status} />
                      <span className="text-soClean-140 text-xs">
                        {injury.status}
                      </span>
                    </div>
                  )}
                  <span className="text-2xs whitespace-nowrap">
                    {DateTime.fromISO(article.posted_at).toFormat(
                      'LLL dd, yyyy'
                    )}
                  </span>
                </div>
              </PlayerProfile>
            );
          })}

          {isNewsLoading && (
            <Loading loadingMessage="Loading team-related news for the past two months" />
          )}
          {isNewsSuccess && news.length === 0 && (
            <NoResults label="No recent news" />
          )}
        </CardContent>
      )}
    </Card>
  );
};
